import { useCallback, useEffect, useState } from "react";
import { Output } from "valibot";

import { tryParseURL } from "components/SonicBot/useHandleEvents/functions";
import { defaultParentWindowSize, parentUrlsCallbacks } from "shared/constants";
import { ParentWindowSizeSchema } from "shared/schemas";
import {
  AppConfiguration,
  ParentMessageValue,
  ParentUrlCallback,
  PostMessageKey,
} from "shared/types";

import { EventCategory } from "./types";

function useGlobalsValue(config: AppConfiguration) {
  const [widgetOpen, setWidgetOpen] = useState(true);
  const [widgetExpanded, setWidgetExpanded] = useState(false);
  const [parentWindowSize, setParentWindowSize] = useState<
    Output<typeof ParentWindowSizeSchema>
  >(defaultParentWindowSize);
  const [parentWindowScroll, setParentWindowScroll] = useState(0);
  const [currentUserFormData, setCurrentUserFormData] = useState<Record<
    string,
    string
  > | null>(null);

  const { baseOrigin, instanceName, isIframe, pageUrl } = config;

  const parentPostMessage = useCallback(
    (message: PostMessageKey, value: ParentMessageValue) =>
      window.parent?.postMessage(
        JSON.stringify({
          message,
          value,
          instanceName,
        }),
        baseOrigin,
      ),
    [baseOrigin, instanceName],
  );

  const sendAnalyticsEvent = useCallback(
    (event: EventCategory, eventData?: Record<string, string>) => {
      const stringifiedEventData = JSON.stringify({
        ...eventData,
        event,
        instanceName,
      });
      parentPostMessage("analytics", stringifiedEventData);
    },
    [instanceName, parentPostMessage],
  );

  const getParentUrl = useCallback(
    (callback: ParentUrlCallback) => {
      if (isIframe) {
        return callback(tryParseURL(pageUrl));
      } else if (window.parent === window) {
        return callback(new URL(window.location.href));
      }
      parentUrlsCallbacks.push(callback);
      parentPostMessage("get-parent-url", true);
    },
    [isIframe, pageUrl, parentPostMessage],
  );

  useEffect(() => {
    parentPostMessage("widget-ready", true);
  }, [parentPostMessage]);

  useEffect(() => {
    parentPostMessage("widget-expanded", widgetExpanded);
  }, [parentPostMessage, widgetExpanded]);

  return {
    widgetOpen,
    setWidgetOpen,
    widgetExpanded,
    setWidgetExpanded,
    parentWindowSize,
    setParentWindowSize,
    currentUserFormData,
    setCurrentUserFormData,
    parentWindowScroll,
    setParentWindowScroll,
    parentPostMessage,
    sendAnalyticsEvent,
    getParentUrl,
  };
}

export default useGlobalsValue;
